import { useState, useRef, useEffect } from 'react';
import { Search, SlidersHorizontal, ChevronDown, X, Pin, Archive, GitFork, Tag } from 'lucide-react';

export default function ChatFilters({
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange,
  forkDepthFilter,
  onForkDepthChange,
  showArchived,
  onShowArchivedChange,
  showPinnedOnly,
  onShowPinnedOnlyChange,
  selectedTags,
  onTagsChange,
  availableTags = [],
  totalCount,
  filteredCount
}) {
  const [showFilters, setShowFilters] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const sortRef = useRef(null);
  const tagRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sortRef.current && !sortRef.current.contains(e.target)) {
        setShowSortDropdown(false);
      }
      if (tagRef.current && !tagRef.current.contains(e.target)) {
        setShowTagDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const sortOptions = [
    { value: 'recent', label: 'Most Recent' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'alphabetical', label: 'Alphabetical' },
    { value: 'forkCount', label: 'Most Forks' }
  ];

  const forkDepthOptions = [
    { value: 'all', label: 'All Chats' },
    { value: 'root', label: 'Root Only' },
    { value: 'hasForks', label: 'Has Forks' },
    { value: 'forks', label: 'Forks Only' }
  ];

  const currentSort = sortOptions.find(o => o.value === sortBy) || sortOptions[0];
  const hasActiveFilters = forkDepthFilter !== 'all' || showArchived || showPinnedOnly || selectedTags.length > 0;

  const toggleTag = (tagName) => {
    if (selectedTags.includes(tagName)) {
      onTagsChange(selectedTags.filter(t => t !== tagName));
    } else {
      onTagsChange([...selectedTags, tagName]);
    }
  };

  const clearAllFilters = () => {
    onForkDepthChange('all');
    onShowArchivedChange(false);
    onShowPinnedOnlyChange(false);
    onTagsChange([]);
    onSearchChange('');
  };

  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      {/* Main filter bar */}
      <div style={{
        display: 'flex',
        gap: 'var(--space-3)',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        {/* Search input */}
        <div style={{
          position: 'relative',
          flex: '1 1 250px',
          minWidth: '200px',
          maxWidth: '400px'
        }}>
          <Search
            size={18}
            style={{
              position: 'absolute',
              left: 'var(--space-3)',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--color-text-tertiary)'
            }}
          />
          <input
            type="text"
            placeholder="Search chats..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            style={{
              width: '100%',
              padding: 'var(--space-2) var(--space-3) var(--space-2) var(--space-8)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--color-surface)',
              color: 'var(--color-text-primary)',
              fontSize: '0.875rem'
            }}
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              style={{
                position: 'absolute',
                right: 'var(--space-2)',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 'var(--space-1)',
                color: 'var(--color-text-tertiary)'
              }}
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Sort dropdown */}
        <div ref={sortRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setShowSortDropdown(!showSortDropdown)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-2) var(--space-3)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--color-surface)',
              color: 'var(--color-text-primary)',
              cursor: 'pointer',
              fontSize: '0.875rem',
              whiteSpace: 'nowrap'
            }}
          >
            <span>{currentSort.label}</span>
            <ChevronDown size={14} />
          </button>
          {showSortDropdown && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: 'var(--space-1)',
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 100,
              minWidth: '150px'
            }}>
              {sortOptions.map(option => (
                <button
                  key={option.value}
                  onClick={() => {
                    onSortChange(option.value);
                    setShowSortDropdown(false);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: 'var(--space-2) var(--space-3)',
                    border: 'none',
                    backgroundColor: sortBy === option.value ? 'var(--primary-50)' : 'transparent',
                    color: sortBy === option.value ? 'var(--primary-700)' : 'var(--color-text-primary)',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '0.875rem'
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Filter toggle button */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-3)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            backgroundColor: hasActiveFilters ? 'var(--primary-50)' : 'var(--color-surface)',
            color: hasActiveFilters ? 'var(--primary-700)' : 'var(--color-text-primary)',
            cursor: 'pointer',
            fontSize: '0.875rem'
          }}
        >
          <SlidersHorizontal size={16} />
          <span>Filters</span>
          {hasActiveFilters && (
            <span style={{
              backgroundColor: 'var(--primary-600)',
              color: 'white',
              borderRadius: '50%',
              width: '18px',
              height: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.7rem',
              fontWeight: 600
            }}>
              {(forkDepthFilter !== 'all' ? 1 : 0) +
               (showArchived ? 1 : 0) +
               (showPinnedOnly ? 1 : 0) +
               selectedTags.length}
            </span>
          )}
        </button>

        {/* Results count */}
        <span style={{
          fontSize: '0.875rem',
          color: 'var(--color-text-tertiary)',
          marginLeft: 'auto'
        }}>
          {filteredCount === totalCount
            ? `${totalCount} chats`
            : `${filteredCount} of ${totalCount} chats`}
        </span>
      </div>

      {/* Expanded filters panel */}
      {showFilters && (
        <div style={{
          marginTop: 'var(--space-3)',
          padding: 'var(--space-4)',
          backgroundColor: 'var(--color-surface-secondary)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-border)'
        }}>
          <div style={{
            display: 'flex',
            gap: 'var(--space-4)',
            flexWrap: 'wrap',
            alignItems: 'flex-start'
          }}>
            {/* Fork depth filter */}
            <div style={{ minWidth: '140px' }}>
              <label style={{
                display: 'block',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--space-2)',
                textTransform: 'uppercase'
              }}>
                <GitFork size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                Fork Status
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                {forkDepthOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => onForkDepthChange(option.value)}
                    style={{
                      padding: 'var(--space-1) var(--space-2)',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      backgroundColor: forkDepthFilter === option.value ? 'var(--primary-100)' : 'transparent',
                      color: forkDepthFilter === option.value ? 'var(--primary-700)' : 'var(--color-text-primary)',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '0.875rem'
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Status toggles */}
            <div style={{ minWidth: '120px' }}>
              <label style={{
                display: 'block',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--space-2)',
                textTransform: 'uppercase'
              }}>
                Status
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  color: 'var(--color-text-primary)'
                }}>
                  <input
                    type="checkbox"
                    checked={showPinnedOnly}
                    onChange={(e) => onShowPinnedOnlyChange(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  <Pin size={14} />
                  Pinned Only
                </label>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  color: 'var(--color-text-primary)'
                }}>
                  <input
                    type="checkbox"
                    checked={showArchived}
                    onChange={(e) => onShowArchivedChange(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  <Archive size={14} />
                  Show Archived
                </label>
              </div>
            </div>

            {/* Tags filter */}
            {availableTags.length > 0 && (
              <div ref={tagRef} style={{ minWidth: '180px', position: 'relative' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: 'var(--color-text-secondary)',
                  marginBottom: 'var(--space-2)',
                  textTransform: 'uppercase'
                }}>
                  <Tag size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                  Tags
                </label>
                <button
                  onClick={() => setShowTagDropdown(!showTagDropdown)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    padding: 'var(--space-2)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-sm)',
                    backgroundColor: 'var(--color-surface)',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    color: 'var(--color-text-primary)'
                  }}
                >
                  <span>
                    {selectedTags.length === 0
                      ? 'Select tags...'
                      : `${selectedTags.length} selected`}
                  </span>
                  <ChevronDown size={14} />
                </button>
                {showTagDropdown && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: 'var(--space-1)',
                    backgroundColor: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-md)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 100,
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}>
                    {availableTags.map(tag => (
                      <label
                        key={tag.name}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          padding: 'var(--space-2) var(--space-3)',
                          cursor: 'pointer',
                          backgroundColor: selectedTags.includes(tag.name) ? 'var(--primary-50)' : 'transparent'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedTags.includes(tag.name)}
                          onChange={() => toggleTag(tag.name)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span style={{ flex: 1, fontSize: '0.875rem' }}>{tag.name}</span>
                        <span style={{
                          fontSize: '0.75rem',
                          color: 'var(--color-text-tertiary)'
                        }}>
                          ({tag.count})
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Clear all filters */}
          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              style={{
                marginTop: 'var(--space-3)',
                padding: 'var(--space-1) var(--space-2)',
                border: 'none',
                backgroundColor: 'transparent',
                color: 'var(--primary-600)',
                cursor: 'pointer',
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1)'
              }}
            >
              <X size={14} />
              Clear all filters
            </button>
          )}
        </div>
      )}

      {/* Active filter pills */}
      {hasActiveFilters && !showFilters && (
        <div style={{
          display: 'flex',
          gap: 'var(--space-2)',
          marginTop: 'var(--space-2)',
          flexWrap: 'wrap'
        }}>
          {forkDepthFilter !== 'all' && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
              padding: 'var(--space-1) var(--space-2)',
              backgroundColor: 'var(--primary-100)',
              color: 'var(--primary-700)',
              borderRadius: 'var(--radius-full)',
              fontSize: '0.75rem'
            }}>
              <GitFork size={12} />
              {forkDepthOptions.find(o => o.value === forkDepthFilter)?.label}
              <button
                onClick={() => onForkDepthChange('all')}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  color: 'inherit'
                }}
              >
                <X size={12} />
              </button>
            </span>
          )}
          {showPinnedOnly && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
              padding: 'var(--space-1) var(--space-2)',
              backgroundColor: 'var(--primary-100)',
              color: 'var(--primary-700)',
              borderRadius: 'var(--radius-full)',
              fontSize: '0.75rem'
            }}>
              <Pin size={12} />
              Pinned
              <button
                onClick={() => onShowPinnedOnlyChange(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  color: 'inherit'
                }}
              >
                <X size={12} />
              </button>
            </span>
          )}
          {showArchived && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
              padding: 'var(--space-1) var(--space-2)',
              backgroundColor: 'var(--primary-100)',
              color: 'var(--primary-700)',
              borderRadius: 'var(--radius-full)',
              fontSize: '0.75rem'
            }}>
              <Archive size={12} />
              Archived
              <button
                onClick={() => onShowArchivedChange(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  color: 'inherit'
                }}
              >
                <X size={12} />
              </button>
            </span>
          )}
          {selectedTags.map(tag => (
            <span
              key={tag}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
                padding: 'var(--space-1) var(--space-2)',
                backgroundColor: 'var(--primary-100)',
                color: 'var(--primary-700)',
                borderRadius: 'var(--radius-full)',
                fontSize: '0.75rem'
              }}
            >
              <Tag size={12} />
              {tag}
              <button
                onClick={() => onTagsChange(selectedTags.filter(t => t !== tag))}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  color: 'inherit'
                }}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
